import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { divIcon, LatLng, latLng, latLngBounds, Map, MapOptions, marker as leafletMarker, tileLayer, Marker } from 'leaflet';
import 'leaflet.markercluster';
import { environment } from '../../../environments/environment';
import { ProjectService } from '../project.service';
import { AuthService } from '../../auth/auth.service';
import { LoggerService } from '../../core/logger/logger.service';
import { SettingsService } from '../../settings/settings.service';
import { UtilsService } from '../../core/utils/utils.service';
import { TranslateService } from '@ngx-translate/core';
import { Platform } from '@ionic/angular';
import { HttpClient } from '@angular/common/http';
import { Project } from '../project.model';
import { Router } from '@angular/router';
import { DbService } from '../../core/db/db.service';

@Component({
    selector: 'app-project-map',
    templateUrl: './project-map.component.html',
    styleUrls: ['./project-map.component.scss'],
})
export class ProjectMapComponent implements OnInit {
    @Output() onProjectClicked = new EventEmitter<string[]>();
    loadProjects = false;
    loadProjectMarkers = false;
    mapOptions: MapOptions;
    mapCenter: LatLng = latLng(48.673462, 11.051311);
    mapZoom = 0;
    map: Map;
    allProjects: Project[];
    markerClusterData: Marker[] = [];
    markerClusterOptions = {
        spiderfyOnMaxZoom: true,
        showCoverageOnHover: false,
        maxClusterRadius: 35,
    };
    constructor(
        protected authSrv: AuthService,
        private logger: LoggerService,
        public settingsSrv: SettingsService,
        public utils: UtilsService,
        public translateService: TranslateService,
        protected platform: Platform,
        private http: HttpClient,
        private projectSrv: ProjectService,
        private router: Router,
        private db: DbService
    ) {}

    async ngOnInit() {
        this.loadProjects = true;
        this.allProjects = await this.projectSrv.getProjectsOfFolder(null, true);
        this.mapOptions = {
            layers: [
                tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
                    maxZoom: 18,
                    attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>-Mitwirkende',
                    noWrap: true,
                    updateWhenZooming: true,
                    referrerPolicy: 'strict-origin-when-cross-origin',
                }),
            ],
            zoom: this.mapZoom,
            center: this.mapCenter,
        };
        this.loadProjects = false;
    }

    onLeafletMapReady(mapInstance: Map) {
        this.map = mapInstance;
        this.markerClusterData = [];

        setTimeout(async () => {
            this.map.invalidateSize();
            await this.loadMarkers();
        }, 200);
    }

    private async loadMarkers() {
        this.loadProjectMarkers = true;
        let bounds = latLngBounds([]);

        const promises = this.allProjects.map(async (project) => {
            if (
                !project.projectAddress?.street &&
                !project.projectAddress?.city &&
                !project.projectAddress?.zip &&
                !project.projectAddress?.country
            ) {
                this.logger.log('[PROJECT MAP] no address for project: ', project.name, ']');
                return;
            }

            let mapLoc = project.projectAddress.mapLocation;
            if (mapLoc) {
                let pos = latLng(mapLoc.location.lat, mapLoc.location.lng);
                this.addMarkerToMap(pos, project);
                bounds.extend(pos);
            } else {
                await this.refreshMapPosition(project, bounds);
            }
        });
        await Promise.all(promises);
        if (bounds.isValid()) {
            this.map.fitBounds(bounds, { padding: [50, 50] });
        }
        this.loadProjectMarkers = false;
    }

    private addMarkerToMap(pos: LatLng, project) {
        let marker = leafletMarker(pos, {
            icon: divIcon({
                html: `<span class='dp-marker'><i class='fad fa-map-pin dp-marker' style='--fa-primary-color:#e20a0a;--fa-secondary-color: black;--fa-secondary-opacity: 0.7;'></i></span>`,
                iconSize: [36, 36],
                iconAnchor: [18, 36],
                popupAnchor: [0, -35],
            }),
            draggable: false,
        } as any);
        let projectAddress = this.utils.buildProjectAddressString(project.projectAddress);
        let projectNumberChip = project.projectNumber
            ? `  <ion-chip
                    class="project-number-chip"
                    color="primary"
                    outline="true">
                    <fa-icon [icon]="['fal', 'hashtag']"></fa-icon>
                    <ion-label class="chip-label">#${project.projectNumber}</ion-label>
                </ion-chip>`
            : '';
        marker.bindPopup(
            `<ion-card class="map-project-popup">
                        <ion-card-content class="map-project-content">
                            <div>
                                <h2 class="dp-mb-s">${project.name}</h2>
                                <p>${projectAddress}</p>
                            </div>
                            ${projectNumberChip}
                        </ion-card-content>
                         <ion-footer>
                            <ion-toolbar class="dp-pb-0" color="none">
                                <ion-buttons slot="end">
                                    <ion-button size="small" color="primary" fill="clear"id="map-btn-${project._id}">
                                    ${this.translateService.instant('shared.open')}
                                    </ion-button>
                                </ion-buttons>
                            </ion-toolbar>
                        </ion-footer>
                    </ion-card>`
        );

        marker.on('popupopen', () => {
            document.getElementById('map-btn-' + project._id)?.addEventListener('click', () => {
                this.router.navigate(['/project', project._id, 'jobs']);
                this.onProjectClicked.emit();
            });
        });

        this.markerClusterData = [...this.markerClusterData, marker];
    }

    async refreshMapPosition(project, bounds) {
        let parts = [
            project.projectAddress.street,
            project.projectAddress.zip,
            project.projectAddress.city,
            project.projectAddress.country,
        ].filter(Boolean);
        let targetAddress = parts.join(' ');

        if (!targetAddress || targetAddress == '') {
            this.logger.log('[PROJECT MAP] no address for project: ', project.name);
            return;
        }

        try {
            const response = await this.http
                .get<any>(
                    `https://maps.googleapis.com/maps/api/geocode/json?address="${encodeURIComponent(targetAddress)}"&region=at&key=${
                        environment.GOOGLE_GEOCODING_KEY
                    }`
                )
                .toPromise();

            if (response.status == 'OK') {
                let firstHit = response.results.shift();
                if (firstHit.geometry.location_type == 'APPROXIMATE') {
                    this.logger.log('[PROJECT MAP] no exact address for project: ', project.name, '- use approximate instead]');
                }

                // save the map location to the project if not already set
                if (!project.projectAddress.mapLocation) {
                    project.projectAddress.mapLocation = firstHit.geometry;
                    try {
                        await this.db
                            .update(project._id, {
                                projectAddress: {
                                    ...project.projectAddress,
                                    mapLocation: firstHit.geometry,
                                },
                            })
                            .toPromise();
                    } catch (e) {
                        this.logger.log('[PROJECT MAP] map location could not be saved to project: ', project.name);
                    }
                }

                let pos = latLng(firstHit.geometry.location.lat, firstHit.geometry.location.lng);
                this.addMarkerToMap(pos, project);
                bounds.extend(pos);
            } else {
                if (response.status == 'REQUEST_DENIED') {
                    this.logger.log('[PROJECT MAP] request denied for getting project location: ', project.name);
                }
            }
        } catch (err) {
            this.logger.error('[PROJECT MAP]  Failed getting address location', err);
        }
    }
}
